import CoreUtils from '../utils/CoreUtils';

import EnergyValueMask from './EnergyValueMask';
import EnergyValueUtils from './EnergyValueUtils';

export default class EnergyValue {
  static Zero = new EnergyValue(0);

  constructor(value) {
    if (EnergyValueUtils.isValidValue(value))
      this.values = [value];
    else if (EnergyValueUtils.isValidValueArray(value))
      this.values = EnergyValueUtils.normalizeValues(value);
    else
      this.values = [0];

    var valueStatistics = EnergyValueUtils.calculateValueStatistics(this.values);

    this.averageValue = valueStatistics.averageValue;
    this.averageDuration = valueStatistics.averageDuration;
    this.minimumValue = valueStatistics.minimumValue;
    this.maximumValue = valueStatistics.maximumValue;
    this.value = Math.round(this.averageValue);
    this.resolution = this.values.length;
    this.isEmpty = this.values.every(value => value === 0);

    Object.freeze(this);
  }

  serialize() {
    return this.values.length > 1
      ? this.values
      : this.values[0];
  }

  isCompatibleResolution(resolution) {
    return EnergyValueUtils.isCompatibleResolution(this.resolution, resolution);
  }

  overwrite(energyValue, mask, maximumResolution) {
    if (!(energyValue instanceof EnergyValue) || !(mask instanceof EnergyValueMask))
      return this;

    return new EnergyValue(
      EnergyValueUtils.overwriteValues(
        this.values,
        energyValue.values,
        mask.maskValues,
        EnergyValueUtils.averageValue,
        maximumResolution
      )
    );
  }

  validateMinimum(minimumEnergyValue) {
    return this.validateAllByCondition(minimumEnergyValue, (value, reference) => value >= reference);
  }

  validateMaximum(maximumEnergyValue) {
    return this.validateAllByCondition(maximumEnergyValue, (value, reference) => value <= reference);
  }

  validateAllByCondition(referenceEnergyValue, conditionFunc) {
    if (!(referenceEnergyValue instanceof EnergyValue) || typeof conditionFunc !== 'function')
      return this;

		var commonResolution = EnergyValueUtils.findCommonResolution(this.resolution, referenceEnergyValue.resolution);

		var values = EnergyValueUtils.upscaleResolution(this.values, commonResolution);
		var referenceValues = EnergyValueUtils.upscaleResolution(referenceEnergyValue.values, commonResolution);

		for (var index = 0; index < commonResolution; index++)
		{
			if (!conditionFunc(values[index], referenceValues[index]))
				return false;
		}

		return true;
  }

  applyMinimum(minimumEnergyValue, maximumResolution) {
    return this.intersect(minimumEnergyValue, Math.max, maximumResolution);
  }

  applyMaximum(maximumEnergyValue, maximumResolution) {
    return this.intersect(maximumEnergyValue, Math.min, maximumResolution);
  }

  intersect(energyValue, intersectFunc, maximumResolution) {
    if (!(energyValue instanceof EnergyValue))
      return this;

    return new EnergyValue(
      EnergyValueUtils.intersect
      (
        this.values,
        energyValue.values,
        intersectFunc,
        maximumResolution
      )
    );
  }

  rescale(targetResolution) {
    return new EnergyValue(
      EnergyValueUtils.rescale
      (
        this.values,
        EnergyValueUtils.averageValue,
        targetResolution
      )
    );
  }

  getEqualityMask(energyValue) {
    return new EnergyValueMask (
      EnergyValueUtils.getEqualityMask(this.values, energyValue.values)
    );
  }

  equals(energyValue, equalityMask) {
    if (CoreUtils.isUndefinedOrNull(energyValue))
      return false;

    if (!(energyValue instanceof EnergyValue))
      return false;

    if (CoreUtils.isDefinedAndNotNull(equalityMask) && equalityMask instanceof EnergyValueMask)
      return EnergyValueUtils.AreValuesEqual(this.values, energyValue.values, equalityMask.maskValues);

    if (energyValue.values.length !== this.values.length)
      return false;

    for (var index = 0; index < this.values.length; index++) {
      if (this.values[index] !== energyValue.values[index])
        return false;
    }

    return true;
  }

  static sum(energyValues, targetResolution) {
	  return EnergyValue.operateOnMultipleValues(
      energyValues,
      EnergyValueUtils.summationValue,
      targetResolution
    );
  }

  static average(energyValues, targetResolution) {
    return EnergyValue.operateOnMultipleValues(
      energyValues,
      EnergyValueUtils.averageValue,
      targetResolution
    );
  }

  static maximum(energyValues, targetResolution) {
    return EnergyValue.operateOnMultipleValues(
      energyValues,
      EnergyValueUtils.maximumValue,
      targetResolution
    );
  }

  static minimum(energyValues, targetResolution) {
		return EnergyValue.operateOnMultipleValues(
      energyValues,
      EnergyValueUtils.minimumValue,
      targetResolution
    );
  }
		
	static operateOnMultipleValues(energyValues, operation, targetResolution) {
    return new EnergyValue(
      EnergyValueUtils.operateOnMultipleValues(
        energyValues.map(energyValue => energyValue.values),
        operation,
        targetResolution
      )
    );
  }
}